/**
 * 다국어 지원을 위한 LocaleContext
 * 런타임에 언어를 전환할 수 있으며, TypeScript 타입 체킹과 IDE 참조 추적을 완벽하게 지원
 * localStorage에 언어 설정을 저장하며, TrpcProvider 내부의 LocaleSyncManager가 app.json5와 동기화
 */

import React, { createContext, useCallback, useEffect, useMemo } from 'react';
import type { LocaleConfig, LocaleResources, SupportedLocale } from '../locales/types';
import * as koResources from '../locales/ko';
import * as enResources from '../locales/en';
import { setCurrentLocale } from '../common/i18n';
import { useLocalStorage } from '../hooks/use-local-storage';

/**
 * 로케일 Context
 */
export const LocaleContext = createContext<LocaleConfig | undefined>(undefined);

/**
 * 언어별 리소스 맵
 */
const localeResourcesMap: Record<SupportedLocale, LocaleResources> = {
  ko: koResources,
  en: enResources,
};

/**
 * LocaleProvider Props
 */
interface LocaleProviderProps {
  children: React.ReactNode;
  /** 초기 언어 (기본값: localStorage에서 로드, 없으면 브라우저 언어 감지) */
  defaultLocale?: SupportedLocale;
}

/**
 * 브라우저 언어 감지
 */
const getBrowserLocale = (): SupportedLocale => {
  if (typeof window === 'undefined') return 'ko';
  const lang = navigator.language.split('-')[0];
  return lang === 'en' ? 'en' : 'ko';
};

/**
 * 다국어 지원을 위한 Provider 컴포넌트
 * localStorage에 언어 설정을 저장하여 재시작 후에도 유지됩니다.
 * TrpcProvider 내부의 LocaleSyncManager가 app.json5와 자동 동기화합니다.
 *
 * @example
 * ```tsx
 * <LocaleProvider>
 *   <App />
 * </LocaleProvider>
 * ```
 */
export function LocaleProvider({ children, defaultLocale }: LocaleProviderProps) {
  // localStorage를 사용하여 언어 저장 (TrpcProvider 없이도 작동)
  const [currentLocale, setCurrentLocaleState] = useLocalStorage<SupportedLocale>(
    'locale',
    defaultLocale ?? getBrowserLocale(),
  );

  // 전역 locale 업데이트
  useEffect(() => {
    setCurrentLocale(currentLocale);
  }, [currentLocale]);

  const setLocale = useCallback(
    (locale: SupportedLocale, _from: string) => {
      console.log(`★★ LocaleProvider setLocale ${locale}`);
      setCurrentLocaleState(locale);

      // 전역 현재 locale도 업데이트 (순수 함수에서 사용)
      setCurrentLocale(locale);

      // dayjs 로케일도 함께 변경
      if (typeof window !== 'undefined') {
        import('dayjs').then(({ default: dayjs }) => {
          dayjs.locale(locale);
        });
      }
    },
    [setCurrentLocaleState],
  );

  const value = useMemo<LocaleConfig>(
    () => ({
      currentLocale,
      setLocale,
      resources: localeResourcesMap[currentLocale],
    }),
    [currentLocale, setLocale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}
